import { useEffect, useMemo, useRef, useState } from 'react'

import { Award01Icon, Camera01Icon, Edit02Icon } from '@hugeicons/react'
import InfiniteScroll from 'react-infinite-scroll-component'

import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useSessionContext } from '@/shared/hooks'
import { useAppRouter } from '@/shared/hooks/useAppRouter'
import { effectiveFilter, visibleFilterOptions } from '@/shared/lib/list-view'
import { AppIcon } from '@/shared/ui/icons'
import { FilterChips, PageHero, Screen } from '@/shared/ui/Layout'
import { PageFilterChipsSkeleton } from '@/shared/ui/Skeleton'

import { FeedEmptyState } from './FeedEmptyState'
import { FeedStatBar } from './FeedStatBar'
import { PostCard } from './PostCard'
import { FEED_FILTER_OPTIONS } from '../lib/feedFilterOptions'
import { filterPosts, matchesFeedFilter } from '../model/filtering'
import { useFeedConfig, useFeedPostsInfinite } from '../model/queries'
import { resolveFocusStep } from '../model/resolveFocusStep'

// Duração do anel de destaque do post focado por deep link (§5.2 do design).
const HIGHLIGHT_DURATION_MS = 2000

// 44×44 nos atalhos: é o caminho principal de publicação do participante.
const SHORTCUT_CLASS =
  'flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-white text-gray-700 shadow-sm transition-colors hover:border-primary/40 hover:text-primary'

const PostFormTrigger = () => {
  const { user } = useSessionContext()
  const router = useAppRouter()

  const navigate = () => router.push('/feed/nova-publicacao')

  return (
    <div className="flex items-center gap-2 py-3">
      <div
        aria-hidden
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/20 text-base font-bold text-primary"
      >
        {user?.name?.[0]?.toUpperCase() || '?'}
      </div>
      <button
        type="button"
        onClick={navigate}
        className="min-h-11 min-w-0 flex-1 rounded-xl border border-border bg-white px-3 text-left text-base text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
      >
        Compartilhe algo...
      </button>
      <div className="flex shrink-0 gap-1">
        <button type="button" onClick={navigate} className={SHORTCUT_CLASS} aria-label="Publicar uma foto">
          <Camera01Icon size={18} variant="stroke" />
        </button>
        <button type="button" onClick={navigate} className={SHORTCUT_CLASS} aria-label="Publicar um texto">
          <Edit02Icon size={18} variant="stroke" />
        </button>
        <button type="button" onClick={navigate} className={SHORTCUT_CLASS} aria-label="Publicar uma conquista">
          <Award01Icon size={18} variant="stroke" />
        </button>
      </div>
    </div>
  )
}

const PostList = ({ posts, allowedEmojis, highlightedPostId, focusPostId, openComments }) => (
  <div className="flex flex-col gap-2 pb-8">
    {posts.map(post => (
      <PostCard
        key={post.id}
        post={post}
        allowedEmojis={allowedEmojis}
        highlighted={post.id === highlightedPostId}
        initiallyExpandComments={post.id === focusPostId && !!openComments}
      />
    ))}
  </div>
)

/**
 * Núcleo reaproveitável do feed — extraído de `FeedPage` para ser montado
 * tanto em `/feed` quanto na home em modo `feed` (`DigitalProductHome`).
 * Diferença em relação ao `FeedPage` anterior: a fonte de posts passa de
 * `useFeedPosts` (fetch único) para `useFeedPostsInfinite` (scroll infinito
 * real, `design.md` §4.2) — o resto (hero, filtros, `PostFormTrigger`, lista)
 * é a mesma composição, sem duplicar markup.
 */
export const FeedExperience = ({ focusPostId = undefined, openComments = false } = {}) => {
  const { eventSlug } = useDigitalProductContext()
  const { record: config } = useFeedConfig(eventSlug)
  const { posts, isEmpty, isLoading, hasMore, loadMore, isFetchingMore } =
    useFeedPostsInfinite(eventSlug)
  const [activeFilter, setActiveFilter] = useState('todos')
  const [pagesLoadedForFocus, setPagesLoadedForFocus] = useState(0)
  const [highlightedPostId, setHighlightedPostId] = useState(null)
  const focusHandledRef = useRef(false)
  // `InfiniteScroll` escuta scroll do `window` por padrão, mas quem rola de
  // fato é o div interno de `Screen.Content` (`layout-scroll-body`,
  // `overflow-y-auto`) — sem isso `next` nunca dispara. `scrollBodyRef`
  // aceita callback ref (React trata função e RefObject igual em `ref`,
  // mesmo a prop de `Screen.Content` sendo tipada só como RefObject) —
  // `setScrollableTarget` já é a assinatura certa: React chama com o nó do
  // DOM assim que ele monta, sem precisar de effect/polling.
  const [scrollableTarget, setScrollableTarget] = useState(null)

  const allowedEmojis = config?.allowed_emojis || []
  const participantPostingEnabled = config?.participant_posting_enabled ?? false

  // Mesmo mecanismo de journeys/missions: chip só aparece com post
  // correspondente, e "todos" sobrevive sempre (allId).
  const visibleFilters = useMemo(
    () =>
      visibleFilterOptions(FEED_FILTER_OPTIONS, id =>
        posts.some(post => matchesFeedFilter(post, id))
      , { allId: 'todos' }),
    [posts]
  )

  // Derivação, não efeito — se o chip ativo sumir (ex.: último post em
  // destaque foi removido), volta pra "todos" no mesmo render.
  const filter = effectiveFilter(activeFilter, visibleFilters, { allId: 'todos' })

  const filteredPosts = useMemo(
    () => filterPosts(posts, filter),
    [posts, filter]
  )

  // Reset se o post de foco mudar (navegação entre deep links sem remount).
  useEffect(() => {
    focusHandledRef.current = false
    setPagesLoadedForFocus(0)
  }, [focusPostId])

  // Timer do anel de destaque isolado num effect próprio, dependente só de
  // `highlightedPostId` (primitivo estável). `posts` muda de referência a
  // cada render (novo array via `.flatMap` em `useFeedPostsInfinite`) — se o
  // timeout vivesse no effect de busca abaixo, cada re-render cancelaria o
  // `setTimeout` anterior (cleanup do useEffect) e o guard de
  // `focusHandledRef` impediria recriar um novo, deixando o anel preso para
  // sempre. Isolado aqui, só reseta quando o post destacado de fato muda.
  useEffect(() => {
    if (!highlightedPostId) return undefined
    const timeout = setTimeout(() => setHighlightedPostId(null), HIGHLIGHT_DURATION_MS)
    return () => clearTimeout(timeout)
  }, [highlightedPostId])

  // Sem `GET /event_posts/{id}` na api, só é possível focar um post que já
  // esteja entre as páginas carregadas — busca mais páginas até achar ou até
  // `MAX_FOCUS_EXTRA_PAGES` (design.md §5.2).
  useEffect(() => {
    if (!focusPostId || focusHandledRef.current || isLoading || isFetchingMore) return undefined

    const { found, shouldLoadMore } = resolveFocusStep({
      posts,
      focusPostId,
      hasMore,
      pagesLoadedForFocus
    })

    if (found) {
      focusHandledRef.current = true
      document
        .getElementById(`post-${focusPostId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setHighlightedPostId(focusPostId)
      return undefined
    }

    if (shouldLoadMore) {
      setPagesLoadedForFocus(n => n + 1)
      loadMore()
    }

    return undefined
  }, [focusPostId, posts, hasMore, isLoading, isFetchingMore, pagesLoadedForFocus, loadMore])

  return (
    <>
      {!isLoading && (
        <Screen.Context fullBleed>
          <PageHero
            variant="full"
            title="Feed Social"
            compact
            statBar={
              <FeedStatBar
                compact
                postCount={posts.length}
                commentCount={posts.reduce((sum, p) => sum + (p.comments_count ?? 0), 0)}
              />
            }
          />
        </Screen.Context>
      )}

      <Screen.Sticky>
        {isLoading && <PageFilterChipsSkeleton />}
        {!isLoading && !isEmpty && (
          <>
            {participantPostingEnabled && <PostFormTrigger />}
            <FilterChips
              options={visibleFilters}
              value={filter}
              onChange={setActiveFilter}
              aria-label="Filtrar publicações"
              className={participantPostingEnabled ? 'pb-3' : undefined}
            />
          </>
        )}
      </Screen.Sticky>

      <Screen.Content className="pt-4" scrollBodyRef={setScrollableTarget}>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : filteredPosts.length === 0 && isEmpty ? (
          <FeedEmptyState />
        ) : filteredPosts.length === 0 ? (
          <div className="py-10 text-center">
            <p className="text-base text-gray-700">
              Nenhuma publicação nessa categoria.
            </p>
          </div>
        ) : scrollableTarget ? (
          // `InfiniteScroll` só lê `scrollableTarget` uma vez, no seu próprio
          // `componentDidMount` — não reage a mudança de prop depois
          // (`componentDidUpdate` só olha `dataLength`). Por isso só monta
          // depois que `scrollBodyRef` já anexou o nó real de scroll; montar
          // antes travaria `this.el` em `window` para sempre, e `next` nunca
          // dispararia com o scroll acontecendo dentro do `Screen.Content`.
          <InfiniteScroll
            dataLength={filteredPosts.length}
            className="overflow-initial"
            style={{ overflow: 'initial' }}
            next={loadMore}
            hasMore={hasMore}
            scrollableTarget={scrollableTarget}
            loader={
              <div className="flex w-full items-center justify-center gap-2 py-4">
                <AppIcon name="spinner-third" className="text-base text-gray-500" />
                <span className="text-sm text-gray-700">Carregando mais publicações...</span>
              </div>
            }
          >
            <PostList
              posts={filteredPosts}
              allowedEmojis={allowedEmojis}
              highlightedPostId={highlightedPostId}
              focusPostId={focusPostId}
              openComments={openComments}
            />
          </InfiniteScroll>
        ) : (
          <PostList
            posts={filteredPosts}
            allowedEmojis={allowedEmojis}
            highlightedPostId={highlightedPostId}
            focusPostId={focusPostId}
            openComments={openComments}
          />
        )}
      </Screen.Content>
    </>
  )
}
